import { NativeAdmob } from 'native-admob';
import { AdsProvider, InterstitialFailure } from './ads.provider';

export class AdMobProvider implements AdsProvider {
    private static readonly FORCE_PLACEHOLDER_BANNER_WIDTH = 720;
    private static readonly FORCE_PLACEHOLDER_BANNER_HEIGHT = 112;
    private static readonly INTERSTITIAL_CLOSE_DELAY_SECONDS = 5;
    private static readonly INTERSTITIAL_PLACEHOLDER_SRC = '/assets/images/ad_interstitial_placeholder.png';
    private currentContainerId?: string;
    private bannerLoadedListener?: any;
    private bannerFailedListener?: any;
    private bannerPlaceholderDisplayedListener?: () => void;
    private bannerPlaceholderVisibilityListener?: (containerId: string, isVisible: boolean) => void;
    private interstitialLoadedListener?: any;
    private interstitialFailedListener?: any;
    private interstitialShownListener?: any;
    private interstitialDismissedListener?: any;
    private interstitialPlaceholderDisplayedListener?: () => void;
    private interstitialPlaceholderClickListener?: () => void;
    private interstitialDismissedCallback?: (placementId: string) => void;
    private interstitialShownCallback?: (placementId: string, provider: string) => void;
    private interstitialShowFailedCallback?: (placementId: string, failure: InterstitialFailure) => void;
    private forcePlaceholderAdsEnabled = false;
    private hasLoadedBanner = false;
    private pendingBannerResumeAfterModal = false;
    private isModalOpen = false;
    private lastBannerHeight = 0;
    private lastBannerWidth = 0;
    private lastExtraNativeOffsetTop = 0;
    private interstitialPlacementId?: string;
    private interstitialProvider = 'admob';
    private pendingInterstitialPreload?: {
        promise: Promise<void>;
        resolve: () => void;
        reject: (error: Error) => void;
    };
    private shouldShowInterstitialPlaceholderOnFailure = false;
    private interstitialOverlayEl?: HTMLDivElement;
    private interstitialCountdownIntervalId?: number;

    async init(): Promise<void> {
        await NativeAdmob.initialize({
            tagForChildDirectedTreatment: true,
            tagForUnderAgeOfConsent: true,
        });

        this.bannerLoadedListener = await NativeAdmob.addListener('bannerLoaded', (info: any) => {
            this.hasLoadedBanner = true;
            const { height: cssHeight, width: cssWidth, extraNativeOffsetTop: cssExtraNativeOffsetTop } = this.toCssPixels(
                info.height,
                info.width,
                info.extraNativeOffsetTop,
            );
            this.lastBannerHeight = cssHeight;
            this.lastBannerWidth = cssWidth;
            this.lastExtraNativeOffsetTop = cssExtraNativeOffsetTop;
            this.notifyBannerPlaceholderVisibility(false);
            this.applyBannerLoadedStyles(cssHeight, cssWidth, cssExtraNativeOffsetTop);
            if (this.isModalOpen || this.hasOpenModal()) {
                this.pendingBannerResumeAfterModal = true;
                this.removeBannerLoadedStyles();
                void NativeAdmob.hideBanner();
                return;
            }
        });

        this.bannerFailedListener = await NativeAdmob.addListener('bannerFailed', () => {
            // The plugin can emit `bannerFailed` during refresh while banner remains visible.
            // If we already had a loaded banner, preserve the current layout styles.
            if (this.hasLoadedBanner || this.lastBannerHeight > 0) {
                this.hideBannerPlaceholder();
                document.body.classList.remove('ad-banner-initializing');
                return;
            }
            this.showBannerPlaceholder();
        });

        this.interstitialLoadedListener = await NativeAdmob.addListener('interstitialLoaded', (info: any) => {
            this.interstitialProvider = info?.provider || this.interstitialProvider;
            const pendingPreload = this.pendingInterstitialPreload;
            this.pendingInterstitialPreload = undefined;
            pendingPreload?.resolve();
        });

        this.interstitialFailedListener = await NativeAdmob.addListener('interstitialFailed', (info: any) => {
            const pendingPreload = this.pendingInterstitialPreload;
            this.pendingInterstitialPreload = undefined;
            pendingPreload?.reject(new Error(info?.error || 'Interstitial preload failed'));

            if (!this.shouldShowInterstitialPlaceholderOnFailure) {
                return;
            }

            this.shouldShowInterstitialPlaceholderOnFailure = false;
            this.showInterstitialPlaceholder();
            if (this.interstitialPlacementId) {
                this.interstitialShowFailedCallback?.(this.interstitialPlacementId, {
                    failureReason: 'unknown',
                    placeholderShown: true,
                    providerErrorMessage: info?.error || 'Interstitial preload failed',
                });
            }
        });

        this.interstitialShownListener = await NativeAdmob.addListener('interstitialShown', (info: any) => {
            this.shouldShowInterstitialPlaceholderOnFailure = false;
            this.removeInterstitialOverlay();
            if (this.interstitialPlacementId) {
                this.interstitialShownCallback?.(
                    this.interstitialPlacementId,
                    info?.provider || this.interstitialProvider,
                );
            }
        });

        this.interstitialDismissedListener = await NativeAdmob.addListener('interstitialDismissed', () => {
            this.shouldShowInterstitialPlaceholderOnFailure = false;
            if (this.interstitialPlacementId) {
                this.interstitialDismissedCallback?.(this.interstitialPlacementId);
            }
        });
    }

    // =============================
    // ========= BANNER ============
    // =============================

    async showBanner(placementId: string, containerId: string) {
        if (this.currentContainerId && this.currentContainerId !== containerId) {
            this.notifyBannerPlaceholderVisibility(false, this.currentContainerId);
        }
        this.currentContainerId = containerId;

        const container = document.getElementById(containerId);
        if (!container) return;

        if (this.forcePlaceholderAdsEnabled) {
            this.showBannerPlaceholder(containerId);
            return;
        }

        this.hideBannerPlaceholder(containerId);
        //add a class to body to indicate banner is initializing
        document.body.classList.add('ad-banner-initializing');

        const rect = container.getBoundingClientRect();

        await NativeAdmob.showBanner({
            adUnitId: placementId,
            x: Math.round(rect.left),
            y: Math.round(rect.top),
            width: 0, // Use adaptive width
        });
    }

    setBannerPlaceholderDisplayedListener(listener?: () => void): void {
        this.bannerPlaceholderDisplayedListener = listener;
    }

    setBannerPlaceholderVisibilityListener(listener?: (containerId: string, isVisible: boolean) => void): void {
        this.bannerPlaceholderVisibilityListener = listener;
    }

    setForcePlaceholderAdsEnabled(enabled: boolean): void {
        this.forcePlaceholderAdsEnabled = enabled;
    }

    setInterstitialPlaceholderClickListener(listener?: () => void): void {
        this.interstitialPlaceholderClickListener = listener;
    }

    setInterstitialPlaceholderDisplayedListener(listener?: () => void): void {
        this.interstitialPlaceholderDisplayedListener = listener;
    }

    setInterstitialDismissedListener(listener?: (placementId: string) => void): void {
        this.interstitialDismissedCallback = listener;
    }

    setInterstitialShownListener(listener?: (placementId: string, provider: string) => void): void {
        this.interstitialShownCallback = listener;
    }

    setInterstitialShowFailedListener(
        listener?: (placementId: string, failure: InterstitialFailure) => void,
    ): void {
        this.interstitialShowFailedCallback = listener;
    }

    async hideBanner() {
        this.pendingBannerResumeAfterModal = false;
        await NativeAdmob.hideBanner();
        this.hideBannerPlaceholder();
        this.removeBannerLoadedStyles();
    }

    async destroyBanner() {
        this.hideBannerPlaceholder();
        this.currentContainerId = undefined;
        this.hasLoadedBanner = false;
        this.pendingBannerResumeAfterModal = false;
        this.lastBannerHeight = 0;
        this.lastBannerWidth = 0;
        this.lastExtraNativeOffsetTop = 0;
        this.removeBannerLoadedStyles();
        await NativeAdmob.destroyBanner();
    }

    async resumeBanner() {
        if (this.forcePlaceholderAdsEnabled) {
            this.showBannerPlaceholder();
            return;
        }

        this.pendingBannerResumeAfterModal = false;
        await NativeAdmob.resumeBanner();
        // In some cases resume does not emit `bannerLoaded`.
        // Re-apply known banner layout so body class/height stays in sync with visible banner.
        if (this.lastBannerHeight > 0) {
            this.hasLoadedBanner = true;
            this.applyBannerLoadedStyles(this.lastBannerHeight, this.lastBannerWidth, this.lastExtraNativeOffsetTop);
        }
    }

    shouldResumeBannerAfterModal(): boolean {
        return this.pendingBannerResumeAfterModal;
    }

    setModalOpenState(isOpen: boolean): void {
        this.isModalOpen = isOpen;
        if (isOpen && !this.hasLoadedBanner) {
            this.pendingBannerResumeAfterModal = true;
        }
    }

    // =============================
    // ======== INTERSTITIAL =======
    // =============================

    supportsInterstitial(): boolean {
        return true;
    }

    preloadInterstitial(placementId: string): Promise<void> {
        this.interstitialPlacementId = placementId;

        if (this.pendingInterstitialPreload) {
            return this.pendingInterstitialPreload.promise;
        }

        let resolvePreload: () => void;
        let rejectPreload: (error: Error) => void;
        const promise = new Promise<void>((resolve, reject) => {
            resolvePreload = resolve;
            rejectPreload = reject;
        });
        const pendingPreload = {
            promise,
            resolve: resolvePreload!,
            reject: rejectPreload!,
        };
        this.pendingInterstitialPreload = pendingPreload;

        NativeAdmob.preloadInterstitial({
            adUnitId: placementId,
        }).catch(error => {
            if (this.pendingInterstitialPreload !== pendingPreload) {
                return;
            }

            this.pendingInterstitialPreload = undefined;
            pendingPreload.reject(error instanceof Error ? error : new Error(String(error)));
        });

        return promise;
    }

    async showInterstitial(placementId: string) {
        if (this.forcePlaceholderAdsEnabled) {
            this.showInterstitialPlaceholder();
            return;
        }

        this.interstitialPlacementId = placementId;
        this.shouldShowInterstitialPlaceholderOnFailure = true;
        try {
            await NativeAdmob.showInterstitial();
        } catch (err) {
            this.shouldShowInterstitialPlaceholderOnFailure = false;
            this.showInterstitialPlaceholder();
            this.interstitialShowFailedCallback?.(placementId, {
                failureReason: 'notReady',
                placeholderShown: true,
                providerErrorMessage: err instanceof Error ? err.message : String(err),
            });
        }
    }

    // =============================
    // ========= REWARDED ==========
    // =============================

    supportsRewarded(): boolean {
        return false; // Not implemented yet
    }

    async preloadRewarded() {}
    async showRewarded(onReward: () => void) {}

    private applyBannerLoadedStyles(height: number, width: number, extraNativeOffsetTop?: number) {
        document.documentElement.style.setProperty('--var-banner-height', `${height}px`);
        document.documentElement.style.setProperty('--var-banner-width', `${width}px`);
        document.documentElement.style.setProperty('--var-extra-native-offset-top', `${extraNativeOffsetTop}px`);
        document.body.classList.remove('ad-banner-initializing');
        document.body.classList.add('has-ad-banner');
    }

    private removeBannerLoadedStyles() {
        document.documentElement.style.setProperty('--var-banner-height', '0px');
        document.documentElement.style.setProperty('--var-banner-width', '0px');
        document.documentElement.style.setProperty('--var-extra-native-offset-top', '0px');
        document.body.classList.remove('has-ad-banner');
        document.body.classList.remove('ad-banner-initializing');
    }

    private showBannerPlaceholder(containerId = this.currentContainerId) {
        this.bannerPlaceholderDisplayedListener?.();
        this.hasLoadedBanner = false;
        const { height: cssHeight, width: cssWidth } = this.toCssPixels(
            AdMobProvider.FORCE_PLACEHOLDER_BANNER_HEIGHT,
            AdMobProvider.FORCE_PLACEHOLDER_BANNER_WIDTH,
            0
        );
        this.lastBannerHeight = cssHeight;
        this.lastBannerWidth = cssWidth;
        this.lastExtraNativeOffsetTop = 0;
        this.pendingBannerResumeAfterModal = false;
        this.notifyBannerPlaceholderVisibility(true, containerId);
        this.applyBannerLoadedStyles(cssHeight, cssWidth, 0);
    }

    private hideBannerPlaceholder(containerId = this.currentContainerId) {
        this.notifyBannerPlaceholderVisibility(false, containerId);
    }

    private showInterstitialPlaceholder() {
        this.interstitialPlaceholderDisplayedListener?.();
        this.removeInterstitialOverlay();

        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.inset = '0';
        overlay.style.zIndex = '99999';
        overlay.style.background = 'rgba(0, 0, 0, 0.75)';
        overlay.style.display = 'flex';
        overlay.style.alignItems = 'center';
        overlay.style.justifyContent = 'center';
        overlay.style.padding = '16px';
        overlay.style.paddingTop = 'calc(16px + env(safe-area-inset-top, 0px) + var(--var-mobile-banner-offset, 0px))';
        overlay.style.boxSizing = 'border-box';

        const card = document.createElement('div');
        card.style.background = '#fff';
        card.style.borderRadius = '12px';
        card.style.width = 'min(92vw, 360px)';
        card.style.maxHeight = 'calc(88vh - env(safe-area-inset-top, 0px) - var(--var-mobile-banner-offset, 0px))';
        card.style.overflow = 'hidden';
        card.style.padding = '12px';
        card.style.boxSizing = 'border-box';
        card.style.display = 'flex';
        card.style.flexDirection = 'column';
        card.style.gap = '12px';

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.textContent = `Close (${AdMobProvider.INTERSTITIAL_CLOSE_DELAY_SECONDS})`;
        closeBtn.disabled = true;
        closeBtn.style.alignSelf = 'flex-end';
        closeBtn.style.border = '0';
        closeBtn.style.background = 'transparent';
        closeBtn.style.fontSize = '14px';
        closeBtn.style.cursor = 'not-allowed';
        closeBtn.style.opacity = '0.6';
        closeBtn.onclick = () => {
            if (closeBtn.disabled) return;
            this.removeInterstitialOverlay();
        };

        const placeholderCta = document.createElement('div');
        placeholderCta.style.width = '100%';
        placeholderCta.style.cursor = 'pointer';
        placeholderCta.style.overflow = 'scroll';
        placeholderCta.onclick = () => {
            this.removeInterstitialOverlay();
            this.interstitialPlaceholderClickListener?.();
        };

        const placeholderImage = document.createElement('img');
        placeholderImage.src = AdMobProvider.INTERSTITIAL_PLACEHOLDER_SRC;
        placeholderImage.alt = 'Upgrade to remove ads';
        placeholderImage.style.display = 'block';
        placeholderImage.style.width = '100%';
        placeholderImage.style.height = 'auto';
        placeholderImage.style.borderRadius = '12px';

        placeholderCta.appendChild(placeholderImage);
        card.appendChild(closeBtn);
        card.appendChild(placeholderCta);
        overlay.appendChild(card);
        document.body.appendChild(overlay);
        this.interstitialOverlayEl = overlay;

        this.startInterstitialCloseCountdown(closeBtn);
    }

    private toCssPixels(
        height: number,
        width: number,
        extraNativeOffsetTop?: number,
    ): { height: number; width: number; extraNativeOffsetTop: number} {
        const devicePixelRatio = window.devicePixelRatio || 1;

        return {
            height: Math.round((height || 0) / devicePixelRatio),
            width: Math.round((width || 0) / devicePixelRatio),
            extraNativeOffsetTop: Math.round((extraNativeOffsetTop || 0) / devicePixelRatio),
        };
    }

    private startInterstitialCloseCountdown(closeBtn: HTMLButtonElement) {
        if (this.interstitialCountdownIntervalId) {
            window.clearInterval(this.interstitialCountdownIntervalId);
            this.interstitialCountdownIntervalId = undefined;
        }

        let secondsLeft = AdMobProvider.INTERSTITIAL_CLOSE_DELAY_SECONDS;
        this.interstitialCountdownIntervalId = window.setInterval(() => {
            secondsLeft -= 1;
            if (secondsLeft > 0) {
                closeBtn.textContent = `Close (${secondsLeft})`;
                return;
            }

            closeBtn.textContent = 'Close';
            closeBtn.disabled = false;
            closeBtn.style.cursor = 'pointer';
            closeBtn.style.opacity = '1';
            if (this.interstitialCountdownIntervalId) {
                window.clearInterval(this.interstitialCountdownIntervalId);
                this.interstitialCountdownIntervalId = undefined;
            }
        }, 1000);
    }

    private removeInterstitialOverlay() {
        if (this.interstitialCountdownIntervalId) {
            window.clearInterval(this.interstitialCountdownIntervalId);
            this.interstitialCountdownIntervalId = undefined;
        }

        if (this.interstitialOverlayEl?.parentElement) {
            this.interstitialOverlayEl.parentElement.removeChild(this.interstitialOverlayEl);
        }
        this.interstitialOverlayEl = undefined;
    }

    private hasOpenModal() {
        return document.querySelectorAll('simple-modal-wrapper').length > 0;
    }

    private notifyBannerPlaceholderVisibility(isVisible: boolean, containerId = this.currentContainerId) {
        if (!containerId) {
            return;
        }

        this.bannerPlaceholderVisibilityListener?.(containerId, isVisible);
    }
}
